import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Paper,

  Alert,
  Tabs,
  Tab,
  Badge,
  useTheme
} from '@mui/material';
import {
  LocationOn as LocationOnIcon,
  Public as PublicIcon,
  NearMe as NearMeIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Speed as SpeedIcon,
  CloudQueue as StagingIcon,
  Cloud as ProductionIcon,
  ViewModule as ViewModuleIcon,
  Code as DevelopmentIcon
} from '@mui/icons-material';
import { useServiceTesting } from '../../hooks/useServiceTesting';
import { ENVIRONMENTS } from '../../constants/serviceEndpoints';

const MultiEnvironmentServiceDashboard = ({ onServiceSelect }) => {
  const theme = useTheme();
  const { checkAllEnvironmentsHealth } = useServiceTesting();
  const [multiEnvHealthData, setMultiEnvHealthData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [viewMode, setViewMode] = useState('tabs'); // 'tabs' or 'side-by-side'

  const services = [
    {
      key: 'geocoding',
      name: 'Geocoding',
      description: 'Address geocoding and coordinate services',
      icon: <LocationOnIcon />,
      color: 'primary',
      features: ['Address lookup', 'Batch geocoding', 'Reverse geocoding']
    },
    {
      key: 'esri',
      name: 'ESRI Demographics',
      description: 'Geographic and demographic data services',
      icon: <PublicIcon />,
      color: 'secondary',
      features: ['Census data', 'Market analysis', 'Geographic insights']
    },
    {
      key: 'nearby_schools',
      name: 'Nearby Schools',
      description: 'School proximity and competitive analysis',
      icon: <NearMeIcon />,
      color: 'success',
      features: ['Distance calculations', 'Competitive mapping', 'Market share']
    }
  ];

  const environments = [
    {
      key: ENVIRONMENTS.DEVELOPMENT,
      name: 'Development',
      icon: <DevelopmentIcon />,
      color: 'success',
      description: 'Local development environment'
    },
    {
      key: ENVIRONMENTS.STAGING,
      name: 'Staging',
      icon: <StagingIcon />,
      color: 'primary',
      description: 'Development and testing environment'
    },
    {
      key: ENVIRONMENTS.PRODUCTION,
      name: 'Production',
      icon: <ProductionIcon />,
      color: 'error',
      description: 'Live production environment'
    }
  ];

  const loadAllEnvironmentsHealth = useCallback(async () => {
    setIsLoading(true);
    try {
      const healthData = await checkAllEnvironmentsHealth();
      setMultiEnvHealthData(healthData);
    } catch (error) {
      console.error('Failed to load multi-environment health data:', error);
      setMultiEnvHealthData({
        error: error.message,
        staging: { error: 'Failed to load staging health' },
        production: { error: 'Failed to load production health' }
      });
    } finally {
      setIsLoading(false);
    }
  }, [checkAllEnvironmentsHealth]);

  // Only run health check once on component mount
  useEffect(() => {
    let mounted = true;
    
    const runInitialHealthCheck = async () => {
      if (mounted) {
        await loadAllEnvironmentsHealth();
      }
    };
    
    runInitialHealthCheck();
    
    return () => {
      mounted = false;
    };
  }, [loadAllEnvironmentsHealth]);

  const getEnvironmentStatusSummary = (envData) => {
    if (!envData || envData.error) {
      return { total: 0, healthy: 0, unhealthy: 0, status: 'error' };
    }

    const services = Object.values(envData);
    const total = services.length;
    const healthy = services.filter(s => s.status === 'healthy').length;
    const unhealthy = total - healthy;

    return {
      total,
      healthy,
      unhealthy,
      status: unhealthy === 0 ? 'healthy' : (healthy > 0 ? 'degraded' : 'unhealthy')
    };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'degraded': return 'warning';
      case 'unhealthy':
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircleIcon />;
      case 'degraded': return <WarningIcon />;
      case 'unhealthy':
      case 'error': return <ErrorIcon />;
      default: return <SpeedIcon />;
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === 'tabs' ? 'side-by-side' : 'tabs');
  };

  const renderEnvironmentCard = (environment, envData) => {
    const summary = getEnvironmentStatusSummary(envData);
    
    return (
      <Card 
        key={environment.key}
        sx={{ 
          mb: 2,
          border: `2px solid ${theme.palette[environment.color].main}`,
          '&:hover': { boxShadow: 4 }
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {environment.icon}
              <Typography variant="h6" color={environment.color}>
                {environment.name}
              </Typography>
              <Chip
                label={`${summary.healthy}/${summary.total} healthy`}
                color={getStatusColor(summary.status)}
                size="small"
                icon={getStatusIcon(summary.status)}
              />
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {environment.description}
          </Typography>

          {envData?.error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {envData.error}
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {services.map((service) => {
                const serviceHealth = envData?.[service.key] || {};
                const isHealthy = serviceHealth.status === 'healthy';
                
                return (
                  <Grid item xs={12} sm={6} md={4} key={service.key}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: `1px solid ${isHealthy ? theme.palette.success.light : theme.palette.error.light}`,
                        '&:hover': {
                          boxShadow: 2,
                          transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                      onClick={() => onServiceSelect(service.key)}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          {service.icon}
                          <Typography variant="subtitle2" noWrap>
                            {service.name}
                          </Typography>
                          <Chip
                            label={serviceHealth.status || 'unknown'}
                            color={getStatusColor(serviceHealth.status)}
                            size="small"
                          />
                        </Box>
                        
                        {serviceHealth.error && (
                          <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
                            {serviceHealth.error.length > 50 
                              ? `${serviceHealth.error.substring(0, 50)}...` 
                              : serviceHealth.error}
                          </Typography>
                        )}
                        
                        {serviceHealth.details?.timestamp && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Last checked: {new Date(serviceHealth.details.timestamp).toLocaleTimeString()}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderTabView = () => (
    <Box>
      <Tabs value={selectedTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        {environments.map((env, index) => {
          const summary = getEnvironmentStatusSummary(multiEnvHealthData[env.key]);
          return (
            <Tab
              key={env.key}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {env.icon}
                  {env.name}
                  <Badge
                    badgeContent={summary.healthy}
                    color={getStatusColor(summary.status)}
                    max={99}
                  />
                </Box>
              }
            />
          );
        })}
      </Tabs>

      {environments.map((env, index) => (
        <Box key={env.key} hidden={selectedTab !== index}>
          {renderEnvironmentCard(env, multiEnvHealthData[env.key])}
        </Box>
      ))}
    </Box>
  );

  const renderSideBySideView = () => (
    <Grid container spacing={3}>
      {environments.map((env) => (
        <Grid item xs={12} lg={6} key={env.key}>
          {renderEnvironmentCard(env, multiEnvHealthData[env.key])}
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Multi-Environment Service Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={`Switch to ${viewMode === 'tabs' ? 'side-by-side' : 'tabbed'} view`}>
            <IconButton onClick={toggleViewMode}>
              <ViewModuleIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh all environment health checks">
            <span>
              <IconButton onClick={loadAllEnvironmentsHealth} disabled={isLoading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Monitor the health and status of Cloud Run services across development, staging and production environments. 
        Click on any service to begin testing.
      </Typography>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Typography>Loading environment health data...</Typography>
        </Box>
      ) : (
        <>
          {/* Overall Status Summary */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Environment Overview
            </Typography>
            <Grid container spacing={2}>
              {environments.map((env) => {
                const summary = getEnvironmentStatusSummary(multiEnvHealthData[env.key]);
                return (
                  <Grid item xs={12} sm={6} key={env.key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {env.icon}
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {env.name}:
                      </Typography>
                      <Chip
                        label={`${summary.healthy}/${summary.total} services healthy`}
                        color={getStatusColor(summary.status)}
                        size="small"
                        icon={getStatusIcon(summary.status)}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>

          {/* Environment Details */}
          {viewMode === 'tabs' ? renderTabView() : renderSideBySideView()}
        </>
      )}
    </Box>
  );
};

export default MultiEnvironmentServiceDashboard; 